import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
// Deep import (not the `../../workspace` barrel): the barrel's `export * from
// "./components"` pulls in `WorkspaceScreen` -> `Layout` -> `SidebarMenu`, which
// deep-imports `onboarding/hooks/useCompanyConfigEnabled` — a file this module's
// own barrel (`onboarding/hooks/index.ts`) also exports directly. Routing this
// hook's `useWorkspace` import through the full barrel therefore drags the whole
// `layout`/`workspace` component tree into any module that (transitively) imports
// `onboarding/hooks`, which broke `vi.mock("../hooks", …) + vi.importActual(...)`
// partial-mocking in `BulkCreateCompanyScreen.test.tsx` (the mock factory resolved,
// but consumers of `../hooks` outside the factory silently got the real, unmocked
// module instead). Other modules already deep-import `workspace/components/WorkspaceScreen`
// for the same reason (see e.g. `src/thread/components/ThreadView.tsx`); this mirrors
// that precedent for `useWorkspace`.
import { useWorkspace } from "../../workspace/hooks/useWorkspace";
import type {
  DestinationSystem,
  SourceSystem,
  WorkloadView,
} from "../../generated/core-api";

export interface UseOnboardingWorkloadParams {
  /** Company code the workload is scoped to; the query is disabled until this is known (unless `enabled` overrides that gate — see below). */
  companyCode: string | undefined;
  /** Destination platform for this migration project (LADR-039 Slice 5 only has `"christmas"`). */
  destinationSystem: DestinationSystem;
  /** Origin system for the source data, if known (e.g. `chris21`, `xero`, `ps_template`). */
  sourceSystem?: SourceSystem;
  /**
   * Override the default `enabled: Boolean(companyCode)` gate. Work patterns
   * and pay components validate a single `companyCode` up front, so waiting
   * on it before creating/reusing the workload is correct for them. Bulk
   * company-shell creation has no such single code — one CSV batch creates
   * many *new* companies, each with its own `company_code`, so the `W5`
   * reuse key degrades to `(workspace_id, destinationSystem)` alone for that
   * vertical. Pass `true` there so the workload isn't stuck permanently
   * disabled waiting for a `companyCode` that will never arrive. Omit for
   * every other caller — default behaviour (gate on `companyCode`) is
   * unchanged.
   */
  enabled?: boolean;
}

/**
 * Create-or-**reuse** a Tier 1 onboarding workload for
 * `(workspace_id from useWorkspace(), companyCode, destinationSystem)` — LADR-039 decision W5.
 *
 * `listOnboardingWorkloads` only filters by `workspace_id`, so matching on `companyCode` +
 * `destinationSystem` happens client-side; a matching workload is reused instead of creating
 * a duplicate. `createOnboardingWorkload` responds with an `OnboardingWorkload` (no
 * `aggregate_status`), so a freshly created workload is re-fetched via `getOnboardingWorkload`
 * — callers always get a `WorkloadView` regardless of which branch ran.
 *
 * Follows the get-or-provision query-fn idiom in `WorkspaceProvider.tsx`
 * (`src/workspace/providers/WorkspaceProvider.tsx`); see `useCompanyConfigs.ts` for the
 * general query-hook pattern.
 */
export function useOnboardingWorkload({
  companyCode,
  destinationSystem,
  sourceSystem,
  enabled,
}: UseOnboardingWorkloadParams) {
  const { workspace_id } = useWorkspace();

  return useQuery({
    queryKey: [
      "onboardingWorkload",
      workspace_id,
      companyCode,
      destinationSystem,
    ],
    queryFn: async (): Promise<WorkloadView> => {
      const workloads = await api.listOnboardingWorkloads({ workspace_id });
      const existing = workloads.find(
        (workload) =>
          (workload.company_code ?? undefined) === (companyCode ?? undefined) &&
          workload.destination_system === destinationSystem
      );

      if (existing) {
        return existing;
      }

      const created = await api.createOnboardingWorkload({
        workspace_id,
        destination_system: destinationSystem,
        source_system: sourceSystem,
        company_code: companyCode,
      });

      return api.getOnboardingWorkload(created.workloadId);
    },
    enabled: enabled ?? Boolean(companyCode),
  });
}
